import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import BillHistoryPanel from './BillHistoryPanel';
import { pendingBills, enqueueBill, discardBill, syncPending, newClientRef, cacheSet, cacheGet, printLocalReceipt } from '../offline';
import './BillingPOS.css';
const BACKEND = '';

const PAYMENT_OPTIONS = [
  { id: 'cash', label: 'Cash', icon: '₹' },
  { id: 'upi', label: 'UPI', icon: '⌁' },
  { id: 'card', label: 'Card', icon: '▣' },
];

export default function BillingPage() {
  const [products, setProducts] = useState([]);
  const [editingBill, setEditingBill] = useState(null);
  const [combos, setCombos] = useState([]);
  const [productOffers, setProductOffers] = useState([]);
  const [offerPrompt, setOfferPrompt] = useState(null);
  const [cart, setCart] = useState([]);
  const [weighKey, setWeighKey] = useState(null);
  const [customerPhone, setCustomerPhone] = useState('');
  const [extrasOpen, setExtrasOpen] = useState(null);

  const [paymentMode, setPaymentMode] = useState('cash');
  const [isSplitPayment, setIsSplitPayment] = useState(false);
  const [splitAmounts, setSplitAmounts] = useState({
    cash: '',
    upi: '',
    card: '',
  });

  const [billDiscount, setBillDiscount] = useState('');
  const [discountType, setDiscountType] = useState('fixed'); // 'fixed' or 'percent'
  const [lastBill, setLastBill] = useState(null);
  const [todaySales, setTodaySales] = useState(null);
  const [heldBills, setHeldBills] = useState(() => {
    try { return JSON.parse(localStorage.getItem('icecream_held_bills') || '[]'); } catch (e) { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem('icecream_held_bills', JSON.stringify(heldBills)); } catch (e) {}
  }, [heldBills]);

  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showHeld, setShowHeld] = useState(false);
  const [showCartMobile, setShowCartMobile] = useState(false);
  const searchRef = useRef(null);
  const [cashReceived, setCashReceived] = useState('');
  const [discOpen, setDiscOpen] = useState({});
  const [focusMode, setFocusMode] = useState(() => {
    try { return localStorage.getItem('icecream_pos_focus') === '1'; } catch (e) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('icecream_pos_focus', focusMode ? '1' : '0'); } catch (e) {}
  }, [focusMode]);
  const toggleDisc = (key) => setDiscOpen((current) => ({ ...current, [key]: !current[key] }));
  const [addCounts, setAddCounts] = useState(() => {
    try { return JSON.parse(localStorage.getItem('icecream_quick_add') || '{}'); } catch (e) { return {}; }
  });

  const loadProducts = () =>
    api
      .get('/products')
      .then((data) => { setProducts(data); cacheSet('products', data); })
      .catch(() => setProducts(cacheGet('products') || []));

  const loadCombos = () =>
    api
      .get('/combos')
      .then((data) => { cacheSet('combos', data); setCombos(data.filter((c) => c.is_currently_active)); })
      .catch(() => setCombos((cacheGet('combos') || []).filter((c) => c.is_currently_active)));

  const loadProductOffers = () =>
    api.get('/product-offers?active=1')
      .then((data) => { cacheSet('product_offers', data); setProductOffers(data); })
      .catch(() => setProductOffers(cacheGet('product_offers') || []));

  const loadToday = () =>
    api
      .get('/bills')
      .then(setTodaySales)
      .catch(() => setTodaySales(null));

  const [pending, setPending] = useState(() => pendingBills());
  const [online, setOnline] = useState(() => navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [showPending, setShowPending] = useState(false);

  const trySync = async () => {
    if (pendingBills().length === 0) return;
    setSyncing(true);
    try {
      const r = await syncPending();
      if (r.synced) loadToday();
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadProducts();
    loadCombos();
    loadProductOffers();
    loadToday();
    api.get('/bills/receipt-settings').then((s) => cacheSet('receipt_settings', s)).catch(() => {});
    const onQueue = () => setPending(pendingBills());
    const onOnline = () => { setOnline(true); trySync(); loadProducts(); loadCombos(); loadProductOffers(); loadToday(); };
    const onOffline = () => setOnline(false);
    window.addEventListener('offline-queue-changed', onQueue);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    trySync();
    const timer = setInterval(trySync, 30000);
    return () => {
      window.removeEventListener('offline-queue-changed', onQueue);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      clearInterval(timer);
    };
  }, []);

  /* =====================================================
     PRODUCT SEARCH
     ===================================================== */

  const categories = useMemo(() => {
    const values = products.map((p) => p.category).filter((c) => c && String(c).trim());
    return ['All', ...new Set(values)];
  }, [products]);

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();

    return products.filter((p) => {
      const matchesSearch = !term || String(p.name || '').toLowerCase().includes(term);
      const matchesCategory = selectedCategory === 'All' || p.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, search, selectedCategory]);

  /* =====================================================
     CART
     ===================================================== */

  const offerByProduct = useMemo(() => {
    const m = {};
    productOffers.forEach((o) => { if (o.choices && o.choices.length) m[o.product_id] = o; });
    return m;
  }, [productOffers]);

  const chooseOffer = (choice) => {
    if (!offerPrompt) return;
    const { product, offer } = offerPrompt;
    setOfferPrompt(null);
    addToCart(product, { skipOffer: true });
    if (!choice) return;
    setCart((current) => {
      const ex = current.find((i) => i.offer_choice_id === choice.id);
      if (ex) return current.map((i) => (i === ex ? { ...i, quantity: i.quantity + 1 } : i));
      return [...current, {
        product_id: choice.product_id, name: choice.product_name, price: Number(choice.offer_price) || 0,
        quantity: 1, discount: 0, offer_choice_id: choice.id, offer_id: offer.id, offer_main_id: product.id,
      }];
    });
  };

  useEffect(() => {
    if (!offerPrompt) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOfferPrompt(null); return; }
      const n = Number(e.key);
      if (n >= 1 && n <= offerPrompt.offer.choices.length) { e.preventDefault(); e.stopPropagation(); chooseOffer(offerPrompt.offer.choices[n - 1]); }
      if (e.key === '0') { e.preventDefault(); e.stopPropagation(); chooseOffer(null); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerPrompt]);

  // Offer items can never outnumber their main item (e.g. a Cold Milo removed -> its free item goes too)
  useEffect(() => {
    if (!cart.some((i) => i.offer_choice_id)) return;
    const mainQty = (pid) => cart.filter((i) => !i.combo_id && !i.offer_choice_id && i.product_id === pid).reduce((s, i) => s + i.quantity, 0);
    const used = {}; let changed = false;
    const next = cart.map((i) => {
      if (!i.offer_choice_id) return i;
      const left = mainQty(i.offer_main_id) - (used[i.offer_main_id] || 0);
      const q = Math.max(0, Math.min(i.quantity, left));
      used[i.offer_main_id] = (used[i.offer_main_id] || 0) + q;
      if (q !== i.quantity) changed = true;
      return q === i.quantity ? i : { ...i, quantity: q };
    }).filter((i) => !(i.offer_choice_id && i.quantity <= 0));
    if (changed) setCart(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  // A bill opened from Bill History -> Edit items
  useEffect(() => {
    if (!products.length) return;
    let pending = null;
    try { pending = JSON.parse(localStorage.getItem('icecream_edit_bill') || 'null'); } catch (e) { pending = null; }
    if (!pending) return;
    localStorage.removeItem('icecream_edit_bill');
    const skipped = [];
    const lines = [];
    (pending.items || []).forEach((i) => {
      if (i.combo_id || i.is_free) { skipped.push(i.name); return; }
      const p = products.find((x) => x.id === i.product_id);
      if (!p) { skipped.push(i.name); return; }
      const ex = lines.find((l) => l.product_id === p.id);
      if (ex) ex.quantity += i.quantity;
      else lines.push({ product_id: p.id, name: p.name, price: p.selling_price, quantity: i.quantity, discount: 0 });
    });
    setCart(lines);
    setDiscountType('fixed');
    setBillDiscount(Number(pending.discount) > 0 ? String(pending.discount) : '');
    if (pending.payment_mode) setPaymentMode(pending.payment_mode);
    setEditingBill({ id: pending.id, bill_no: pending.bill_no });
    if (skipped.length) alert(`Re-add these by hand (offer/combo items are not copied): ${skipped.join(', ')}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const addToCart = (product, opts = {}) => {
    if (!opts.skipOffer && product.pricing_type !== 'weighted' && offerByProduct[product.id]) {
      setOfferPrompt({ product, offer: offerByProduct[product.id] });
      return;
    }
    if (product.pricing_type === 'weighted') {
      // Add the line (weight 0) and open the weight box in the order panel
      setCart((current) => {
        if (current.find((item) => item.product_id === product.id && !item.combo_id)) return current;
        return [
          ...current,
          { product_id: product.id, name: product.name, price: product.selling_price, quantity: 0, discount: 0 },
        ];
      });
      setWeighKey(`product-${product.id}`);
      if (window.matchMedia && window.matchMedia('(max-width: 900px), (max-height: 520px)').matches) {
        setShowCartMobile(true);
      }
      return;
    }

    setCart((current) => {
      const isMain = (item) => item.product_id === product.id && !item.offer_choice_id && !item.combo_id;
      const existing = current.find(isMain);

      if (existing) {
        return current.map((item) =>
          isMain(item)
            ? {
                ...item,
                quantity: item.quantity + 1,
              }
            : item
        );
      }

      return [
        ...current,
        {
          product_id: product.id,
          name: product.name,
          price: product.selling_price,
          quantity: 1,
          discount: 0,
        },
      ];
    });
  };

  const addComboToCart = (combo) => {
    setCart((current) => {
      const existing = current.find(
        (item) => item.combo_id === combo.id
      );

      if (existing) {
        return current.map((item) =>
          item.combo_id === combo.id
            ? {
                ...item,
                quantity: item.quantity + 1,
              }
            : item
        );
      }

      const freeItem = combo.items.find((item) => item.is_free);

      const label = freeItem
        ? `${combo.name} (+ free ${freeItem.product_name})`
        : combo.name;

      return [
        ...current,
        {
          combo_id: combo.id,
          name: label,
          price: combo.price,
          quantity: 1,
          discount: 0,
          isCombo: true,
        },
      ];
    });
  };

  const getItemKey = (item) =>
    item.combo_id
      ? `combo-${item.combo_id}`
      : item.offer_choice_id
        ? `offer-${item.offer_choice_id}`
        : `product-${item.product_id}`;

  const changeQty = (key, delta) => {
    setCart((current) =>
      current
        .map((item) =>
          getItemKey(item) === key
            ? {
                ...item,
                quantity: item.quantity + delta,
              }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const removeItem = (key) => {
    setCart((current) =>
      current.filter((item) => getItemKey(item) !== key)
    );
  };

  const setLineDiscount = (key, value) => {
    setCart((current) =>
      current.map((item) =>
        getItemKey(item) === key
          ? {
              ...item,
              discount: Number(value) || 0,
            }
          : item
      )
    );
  };

  /* =====================================================
     TOTALS
     ===================================================== */

  const lineTotal = (item) =>
    Math.max(
      0,
      item.price * item.quantity - (item.discount || 0)
    );

  const subtotal = cart.reduce(
    (sum, item) => sum + lineTotal(item),
    0
  );

  const billDiscountAmount = discountType === 'percent'
    ? Math.round(subtotal * ((Number(billDiscount) || 0) / 100) * 100) / 100
    : (Number(billDiscount) || 0);

  const exactTotal = Math.max(0, subtotal - billDiscountAmount);
  const total = Math.round(exactTotal); // nearest rupee, same as the server
  const roundOff = Math.round((total - exactTotal) * 100) / 100;

  const totalItems = cart.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  /* =====================================================
     BILL ACTIONS
     ===================================================== */

  const clearCart = () => {
    setCart([]);
    setBillDiscount('');
    setSplitAmounts({
      cash: '',
      upi: '',
      card: '',
    });
  };

  const holdBill = () => {
    if (cart.length === 0) {
      alert('Cart is empty');
      return;
    }

    setHeldBills((current) => [
      ...current,
      {
        id: Date.now(),
        cart,
        billDiscount,
        paymentMode,
        discountType,
        heldAt: new Date().toLocaleTimeString(),
      },
    ]);

    clearCart();
  };

  const resumeBill = (held) => {
    setCart(held.cart);
    setBillDiscount(held.billDiscount);
    setPaymentMode(held.paymentMode);
    if (held.discountType) setDiscountType(held.discountType);

    setHeldBills((current) =>
      current.filter((bill) => bill.id !== held.id)
    );
  };

  const discardHeld = (id) => {
    setHeldBills((current) =>
      current.filter((bill) => bill.id !== id)
    );
  };

  const completeBill = async (print = false) => {
    if (cart.some((i) => !i.combo_id && !(i.quantity > 0))) {
      alert('Enter the weight for every weighed item before completing the sale.');
      return;
    }
    if (cart.length === 0) {
      alert('Cart is empty');
      return;
    }

    let body = {
      discount: billDiscountAmount,
      lines: cart.map((item) =>
        item.combo_id
          ? {
              combo_id: item.combo_id,
              quantity: item.quantity,
            }
          : item.offer_choice_id
            ? { offer_choice_id: item.offer_choice_id, product_id: item.product_id, quantity: item.quantity }
            : {
              product_id: item.product_id,
              quantity: item.quantity,
              discount: item.discount,
            }
      ),
    };

    if (isSplitPayment) {
      const payments = Object.entries(splitAmounts)
        .filter(([, value]) => value && Number(value) > 0)
        .map(([mode, value]) => ({
          mode,
          amount: Number(value),
        }));

      const splitTotal = payments.reduce(
        (sum, payment) => sum + payment.amount,
        0
      );

      if (payments.length < 2) {
        alert(
          'Enter amounts for at least two payment modes to split'
        );
        return;
      }

      if (Math.abs(splitTotal - total) > 0.01) {
        alert(
          `Split amounts (₹${splitTotal.toFixed(
            2
          )}) must add up to the total (₹${total.toFixed(2)})`
        );
        return;
      }

      body.payments = payments;
    } else {
      body.payment_mode = paymentMode;
    }

    const waPhone = customerPhone.trim() ? normPhone(customerPhone) : null;
    if (customerPhone.trim() && !waPhone) { alert('The customer WhatsApp number does not look right. Enter a 10-digit mobile number or clear it.'); return; }
    if (waPhone) body.customer_phone = waPhone;

    const clientRef = newClientRef();
    body.client_ref = clientRef;
    if (editingBill) body.replaces_bill_id = editingBill.id;

    try {
      setSaving(true);

      const bill = await api.post('/bills', body);

      if (bill.stock_warnings && bill.stock_warnings.length > 0) {
        alert('Sale completed, but stock is now short:\n\n' + bill.stock_warnings.join('\n'));
      }

      setLastBill({ ...bill, wa_phone: waPhone });
      if (editingBill) { alert(`${editingBill.bill_no} was replaced by ${bill.bill_no}.`); setEditingBill(null); }
      if (print) reprintBill(bill.id);
      else if (waPhone) sendWhatsapp({ ...bill, wa_phone: waPhone }, waPhone);
      setCustomerPhone('');

      clearCart();

      setIsSplitPayment(false);

      loadToday();
    } catch (err) {
      if (err.offline) {
        // No connection: keep the sale on this device and upload it later
        const display = {
          items: cart.flatMap((item) => {
            const combo = item.combo_id ? combos.find((c) => c.id === item.combo_id) : null;
            const rows = [{ name: combo ? combo.name : item.name, qty: item.quantity, price: lineTotal(item) }];
            if (combo) {
              (combo.items || []).filter((i) => i.is_free).forEach((f) => {
                rows.push({ name: f.product_name, qty: (f.quantity || 1) * item.quantity, price: 0, free: true });
              });
            }
            return rows;
          }),
          subtotal,
          discount: billDiscountAmount,
          total,
          payment_mode: body.payment_mode || (body.payments && body.payments[0] && body.payments[0].mode),
          payments: body.payments || [],
        };
        const entry = { client_ref: clientRef, client_time: new Date().toISOString(), body, display };
        enqueueBill(entry);
        if (print) printLocalReceipt(entry);
        setCustomerPhone('');
        setLastBill({
          offline: true,
          entry,
          id: null,
          bill_no: clientRef,
          wa_phone: waPhone,
          total_amount: total,
          items: display.items.map((i, idx) => ({ id: idx, product_name: i.name + (i.free ? ' (FREE)' : ''), quantity: i.qty, price: i.price })),
        });
        clearCart();
        setIsSplitPayment(false);
      } else {
        alert(err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const cancelBill = async (billId) => {
    if (
      !window.confirm(
        'Cancel this bill? Stock will be restored.'
      )
    ) {
      return;
    }

    const reason = window.prompt('Reason for cancelling this bill (required):');
    if (!reason || !reason.trim()) {
      alert('A reason is required to cancel a bill');
      return;
    }

    try {
      await api.post(`/bills/${billId}/cancel`, { reason: reason.trim() });

      if (lastBill && lastBill.id === billId) {
        setLastBill(null);
      }

      loadToday();

      alert('Bill cancelled, stock restored');
    } catch (err) {
      alert(err.message);
    }
  };

  const normPhone = (v) => {
    let d = String(v || '').replace(/\D/g, '');
    if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
    if (d.length === 10) d = `91${d}`;
    return d.length >= 11 && d.length <= 13 ? d : null;
  };

  const whatsappText = (b) => {
    const when = (b.bill_date ? new Date(`${String(b.bill_date).replace(' ', 'T')}Z`) : new Date())
      .toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    const lines = (b.items || []).map((i) => {
      const free = !!i.is_free || / \(FREE\)$/.test(i.product_name || '') || Number(i.price) === 0;
      const name = String(i.product_name || '').replace(/ \(FREE\)$/, '');
      if (/parcel/i.test(name)) return `Parcel charge  —  ${fmt2(i.price)}`;
      return `${i.quantity} × ${name}  —  ${free ? 'FREE' : fmt2(i.price)}`;
    });
    const pays = b.payments && b.payments.length > 1
      ? b.payments.map((p) => `${String(p.mode).toUpperCase()} ${fmt2(p.amount)}`).join(' + ')
      : String(b.payment_mode || (b.entry && b.entry.display && b.entry.display.payment_mode) || '').toUpperCase();
    return [
      `🍦 *${document.title || 'Ice Bay'}*`,
      `Bill ${b.offline ? '(saved offline)' : `#${b.day_no || b.bill_no}`} · ${when}`,
      '',
      ...lines,
      '',
      Number(b.discount) > 0 ? `Discount  −${fmt2(b.discount)}` : null,
      `*Total ${fmt2(b.total_amount)}*${pays ? `  (${pays})` : ''}`,
      '',
      'Thank you! Visit again 😊',
    ].filter((x) => x !== null).join('\n');
  };

  const sendWhatsapp = (b, phoneOverride) => {
    let phone = phoneOverride || b.wa_phone;
    if (!phone) {
      const v = window.prompt('Customer WhatsApp number');
      if (!v) return;
      phone = normPhone(v);
      if (!phone) { alert('Enter a valid 10-digit mobile number'); return; }
      setLastBill((lb) => (lb ? { ...lb, wa_phone: phone } : lb));
    }
    window.open(`https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(whatsappText(b))}`, '_blank');
  };

  const reprintBill = (billId) => {
    const token = localStorage.getItem('icecream_token');
    window.open(
      `${BACKEND}/api/bills/${billId}/receipt?token=${token}&device_token=${localStorage.getItem('icecream_device') || ''}`,
      '_blank'
    );
  };


  /* =====================================================
     UI HELPERS (display only — no business logic)
     ===================================================== */

  const fmt = (n) => {
    const v = Number(n) || 0;
    return '₹' + v.toLocaleString('en-IN', {
      minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  };

  const fmt2 = (n) =>
    '₹' + (Number(n) || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const productById = useMemo(() => {
    const map = {};
    products.forEach((p) => { map[p.id] = p; });
    return map;
  }, [products]);

  // Parcel charge = a product whose name contains "parcel" (hidden from the grid)
  const parcelProduct = useMemo(
    () => products.find((p) => /parcel/i.test(p.name) && p.pricing_type !== 'weighted') || null,
    [products]
  );
  const parcelLine = parcelProduct ? cart.find((i) => !i.combo_id && i.product_id === parcelProduct.id) : null;
  const parcelQty = parcelLine ? parcelLine.quantity : 0;
  const parcelPrice = parcelProduct ? Number(parcelProduct.selling_price) || 1 : 1;
  const parcelAmount = parcelQty * parcelPrice;
  const setParcelQty = (q) => {
    if (!parcelProduct) return;
    const qty = Math.max(0, Math.round(q));
    setCart((current) => {
      const ex = current.find((i) => !i.combo_id && i.product_id === parcelProduct.id);
      if (qty === 0) return current.filter((i) => i !== ex);
      if (ex) return current.map((i) => (i === ex ? { ...i, quantity: qty } : i));
      return [...current, { product_id: parcelProduct.id, name: parcelProduct.name, price: parcelProduct.selling_price, quantity: qty, discount: 0 }];
    });
  };

  const setParcelAmount = (v) => setParcelQty((Number(v) || 0) / parcelPrice);

  const comboById = useMemo(() => {
    const map = {};
    combos.forEach((c) => { map[c.id] = c; });
    return map;
  }, [combos]);

  const cartQtyByProduct = useMemo(() => {
    const map = {};
    cart.forEach((item) => {
      if (!item.combo_id) map[item.product_id] = item.quantity;
    });
    return map;
  }, [cart]);

  const isWeighted = (item) =>
    !item.combo_id && productById[item.product_id]?.pricing_type === 'weighted';

  const grossTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemDiscountTotal = Math.max(0, grossTotal - subtotal);

  const splitPaid = Object.values(splitAmounts).reduce((sum, v) => sum + (Number(v) || 0), 0);
  const splitLeft = Math.round((total - splitPaid) * 100) / 100;

  const nextOrderNo = todaySales ? (Number(todaySales.bill_count) || 0) + 1 : null;

  const showOfferStrip =
    activeTab === 'all' && !search && selectedCategory === 'All' && combos.length > 0;

  const CATEGORY_TINTS = ['#EFEAFF', '#E3F6EE', '#FFF1E2', '#E6F1FD', '#FDECF1', '#FFF8D9'];

  const categoryTint = (cat = '') => {
    let h = 0;
    for (let i = 0; i < cat.length; i += 1) h = (h * 31 + cat.charCodeAt(i)) % 997;
    return CATEGORY_TINTS[h % CATEGORY_TINTS.length];
  };

  const categoryIcon = (cat = '') => {
    const c = String(cat).toLowerCase();
    if (c.includes('hendy') || c.includes('chicken')) return '🍗';
    if (c.includes('can')) return '🥫';
    if (c.includes('choc')) return '🍫';
    if (c.includes('cake') || c.includes('dessert')) return '🧁';
    if (c.includes('shake') || c.includes('drink')) return '🥤';
    if (c.includes('water')) return '🧊';
    if (c.includes('ball') || c.includes('tub') || c.includes('family')) return '🍨';
    return '🍦';
  };

  const heldTotal = (held) => {
    const sub = held.cart.reduce((sum, item) => sum + lineTotal(item), 0);
    const disc = held.discountType === 'percent'
      ? sub * ((Number(held.billDiscount) || 0) / 100)
      : Number(held.billDiscount) || 0;
    return Math.max(0, sub - disc);
  };

  const handleResume = (held) => {
    if (cart.length > 0) {
      if (!window.confirm('The current bill has items. Hold it and resume this one?')) return;
      holdBill();
    }
    resumeBill(held);
    setShowHeld(false);
  };

  const editWeight = (item) => setWeighKey(getItemKey(item));

  const setWeight = (key, value) => {
    const grams = Math.max(0, Math.round(Number(value) || 0));
    setCart((current) => current.map((i) => (getItemKey(i) === key ? { ...i, quantity: grams } : i)));
  };

  const handleSearchKey = (e) => {
    if (e.key === 'Enter') {
      if (activeTab === 'all' && filteredProducts.length > 0) {
        handleAdd(filteredProducts[0]);
        setSearch('');
      }
    } else if (e.key === 'Escape') {
      setSearch('');
    }
  };

  // Quick add: learn which products the cashier adds most (saved in this browser)
  const handleAdd = (product) => {
    addToCart(product);
    setAddCounts((current) => {
      const next = { ...current, [product.id]: (current[product.id] || 0) + 1 };
      try { localStorage.setItem('icecream_quick_add', JSON.stringify(next)); } catch (e) {}
      return next;
    });
  };

  const quickAddProducts = useMemo(() => (
    Object.entries(addCounts)
      .filter(([, count]) => count >= 3)
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => productById[id])
      .filter(Boolean)
      .slice(0, 8)
  ), [addCounts, productById]);

  // Cash received / change (display only, does not affect the saved bill)
  useEffect(() => {
    if (cart.length === 0) setCashReceived('');
  }, [cart.length]);

  const cashGiven = Number(cashReceived) || 0;
  const changeDue = Math.round((cashGiven - total) * 100) / 100;
  const quickCash = useMemo(() => {
    if (total <= 0) return [];
    const up = (step) => Math.ceil(total / step) * step;
    const values = [total, up(50), up(100), up(500), up(2000)];
    return [...new Set(values.map((v) => Math.round(v * 100) / 100))].slice(0, 4);
  }, [total]);

  // Keyboard: "/" search, F2 hold, F9 or Ctrl/Cmd+Enter complete
  const completeRef = useRef(null);
  completeRef.current = (print) => {
    if (!saving && cart.length > 0) completeBill(print);
  };
  const holdRef = useRef(null);
  holdRef.current = () => {
    if (!saving && cart.length > 0) holdBill();
  };

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || tag === 'select';
      if (e.key === '/' && !typing) {
        e.preventDefault();
        if (searchRef.current) searchRef.current.focus();
      }
      if (e.key === 'F2') {
        e.preventDefault();
        if (holdRef.current) holdRef.current();
      }
      if (e.key === 'F9' || ((e.ctrlKey || e.metaKey) && e.key === 'Enter')) {
        e.preventDefault();
        if (completeRef.current) completeRef.current(true); // Save & Print
      }
      if (e.key === 'F8') {
        e.preventDefault();
        if (completeRef.current) completeRef.current(false); // Save only
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const renderOfferCard = (combo, size) => {
    const paid = (combo.items || []).filter((i) => !i.is_free);
    const free = (combo.items || []).filter((i) => i.is_free);
    return (
      <button
        key={combo.id}
        type="button"
        className={`pos-offer-card ${size === 'large' ? 'is-large' : ''}`}
        onClick={() => addComboToCart(combo)}
      >
        <span className="pos-offer-tag">Offer</span>
        <span className="pos-offer-name">{combo.name}</span>
        {paid.length > 0 && (
          <span className="pos-offer-items">
            {paid.map((i) => i.product_name).join(' + ')}
          </span>
        )}
        {free.map((i) => (
          <span key={i.product_name} className="pos-offer-free">
            <span className="pos-free">FREE</span> {i.product_name}
          </span>
        ))}
        <span className="pos-offer-foot">
          <span className="pos-offer-price">{fmt(combo.price)}</span>
          <span className="pos-add" aria-hidden="true">+</span>
        </span>
      </button>
    );
  };

  /* =====================================================
     RENDER
     ===================================================== */

  return (
    <div className={`pos-root ${focusMode ? 'is-focus' : ''}`}>
      {offerPrompt && (
        <div className="pos-offer-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setOfferPrompt(null); }}>
          <div className="pos-offer-modal" role="dialog" aria-modal="true" aria-label={`Offer for ${offerPrompt.product.name}`}>
            <span className="pos-offer-tag">🎁 Offer</span>
            <h3>{offerPrompt.product.name}</h3>
            <p>{offerPrompt.offer.name || `Choose one with ${offerPrompt.product.name}`}</p>
            <div className="pos-offer-choices">
              {offerPrompt.offer.choices.map((c, i) => (
                <button key={c.id} type="button" autoFocus={i === 0} onClick={() => chooseOffer(c)}>
                  <kbd>{i + 1}</kbd>
                  <b>{c.product_name}</b>
                  <span className={c.offer_price > 0 ? 'is-price' : 'is-free'}>{c.offer_price > 0 ? fmt(c.offer_price) : 'FREE'}</span>
                </button>
              ))}
            </div>
            <div className="pos-offer-foot">
              <button type="button" className="pos-offer-skip" onClick={() => chooseOffer(null)}>No offer, just {offerPrompt.product.name} <kbd>0</kbd></button>
              <button type="button" className="pos-offer-cancel" onClick={() => setOfferPrompt(null)}>Cancel <kbd>Esc</kbd></button>
            </div>
          </div>
        </div>
      )}

      <div className="pos-layout">

        {/* =================================================
            LEFT — PRODUCTS
            ================================================= */}
        <section className="pos-products">
      {/* TOP BAR */}
      <header className="pos-topbar">
        <div className="pos-title">
          <h1>Billing</h1>
          {nextOrderNo && <span>Order #{nextOrderNo} today</span>}
        </div>

        {todaySales && (
          <div className="pos-stats">
            <div>
              <span>Today's sales</span>
              <strong>{fmt(Number(todaySales.total_sales || 0).toFixed(0))}</strong>
            </div>
            <div>
              <span>Bills</span>
              <strong>{todaySales.bill_count}</strong>
            </div>
          </div>
        )}

        <div className="pos-top-actions">
          {heldBills.length > 0 && (
            <button
              type="button"
              className={`pos-held-btn ${showHeld ? 'is-open' : ''}`}
              onClick={() => setShowHeld((v) => !v)}
            >
              Held bills <b>{heldBills.length}</b>
            </button>
          )}
          <button type="button" className="pos-ghost-btn" onClick={() => setShowHistory(true)}>
            Bill history
          </button>
          <button
            type="button"
            className={`pos-ghost-btn pos-focus-btn ${focusMode ? 'is-on' : ''}`}
            onClick={() => setFocusMode((v) => !v)}
            title={focusMode ? 'Show the menu again' : 'Hide the menu for more space'}
          >
            {focusMode ? 'Exit focus' : 'Focus mode'}
          </button>
        </div>
      </header>

      {/* HELD BILLS */}
      {showHeld && heldBills.length > 0 && (
        <div className="pos-held-panel">
          {heldBills.map((held) => {
            const count = held.cart.reduce((sum, item) => sum + item.quantity, 0);
            return (
              <div key={held.id} className="pos-held-row">
                <div className="pos-held-info">
                  <strong>{fmt2(heldTotal(held))}</strong>
                  <span>{count} {count === 1 ? 'item' : 'items'}, held at {held.heldAt}</span>
                </div>
                <button type="button" className="pos-resume-btn" onClick={() => handleResume(held)}>
                  Resume
                </button>
                <button
                  type="button"
                  className="pos-discard-btn"
                  onClick={() => {
                    if (window.confirm('Discard this held bill?')) discardHeld(held.id);
                  }}
                >
                  Discard
                </button>
              </div>
            );
          })}
        </div>
      )}

      {(!online || pending.length > 0) && (
        <div className={`pos-offline ${!online ? 'is-offline' : pending.some((p) => p.last_error) ? 'is-error' : 'is-pending'}`}>
          <span>
            {!online
              ? `Offline. Sales are being saved on this device${pending.length ? ` (${pending.length} waiting)` : ''} and will upload when the internet is back.`
              : syncing
                ? `Uploading ${pending.length} offline sale(s)…`
                : pending.some((p) => p.last_error)
                  ? `${pending.length} offline sale(s) could not upload. Check them below.`
                  : `${pending.length} offline sale(s) waiting to upload.`}
          </span>
          <span className="pos-offline-actions">
            {online && pending.length > 0 && (
              <button type="button" onClick={trySync} disabled={syncing}>Upload now</button>
            )}
            {pending.length > 0 && (
              <button type="button" onClick={() => setShowPending((v) => !v)}>{showPending ? 'Hide' : 'View'}</button>
            )}
          </span>
        </div>
      )}

      {showPending && pending.length > 0 && (
        <div className="pos-pending-panel">
          {pending.map((p) => (
            <div key={p.client_ref} className="pos-pending-row">
              <div className="pos-pending-info">
                <strong>{fmt2(p.display.total)}</strong>
                <span>{p.client_ref}, {new Date(p.client_time).toLocaleString('en-IN')}</span>
                {p.last_error && <em>Upload failed: {p.last_error}</em>}
              </div>
              <button type="button" onClick={() => printLocalReceipt(p)}>Print</button>
              <button
                type="button"
                className="is-danger"
                onClick={() => { if (window.confirm('Discard this offline sale? It will NOT be recorded anywhere.')) discardBill(p.client_ref); }}
              >
                Discard
              </button>
            </div>
          ))}
        </div>
      )}

      {showHistory && <BillHistoryPanel onClose={() => setShowHistory(false)} />}


          <div className="pos-search">
            <svg className="pos-search-icon" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2.2" />
              <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              placeholder="Search products"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (e.target.value) {
                  setSelectedCategory('All');
                  setActiveTab('all');
                }
              }}
              onKeyDown={handleSearchKey}
              autoFocus
              aria-label="Search products"
            />
            {search ? (
              <button type="button" className="pos-search-clear" onClick={() => setSearch('')} aria-label="Clear search">
                ×
              </button>
            ) : (
              <kbd className="pos-kbd">/</kbd>
            )}
          </div>

          <div className="pos-chips" role="tablist">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className={activeTab === 'all' && selectedCategory === cat ? 'pos-chip is-active' : 'pos-chip'}
                onClick={() => { setSelectedCategory(cat); setActiveTab('all'); }}
              >
                {cat}
              </button>
            ))}
            {combos.length > 0 && (
            <button
              type="button"
              className={activeTab === 'offers' ? 'pos-chip pos-chip-offer is-active' : 'pos-chip pos-chip-offer'}
              onClick={() => setActiveTab('offers')}
            >
              Offers {combos.length > 0 && <b>{combos.length}</b>}
            </button>
            )}
          </div>

          {/* QUICK ADD (most-added products) */}
          {activeTab === 'all' && !search && selectedCategory === 'All' && quickAddProducts.length > 0 && (
            <div className="pos-quick">
              <span className="pos-quick-label">Quick add</span>
              <div className="pos-quick-row">
                {quickAddProducts.map((product) => (
                  <button key={product.id} type="button" className="pos-quick-btn" onClick={() => handleAdd(product)}>
                    <span aria-hidden="true">{categoryIcon(product.category)}</span>
                    <span className="pos-quick-name">{product.name}</span>
                    <b>{product.pricing_type === 'weighted' ? `${fmt(product.selling_price)}/g` : fmt(Number(product.selling_price).toFixed(0))}</b>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* OFFERS STRIP (on the All view) */}
          {showOfferStrip && (
            <div className="pos-offer-strip">
              {combos.map((combo) => renderOfferCard(combo))}
            </div>
          )}

          {/* OFFERS TAB */}
          {activeTab === 'offers' && (
            combos.length === 0 ? (
              <div className="pos-empty">
                <strong>No active offers</strong>
                <span>Create offers from the Offers page.</span>
              </div>
            ) : (
              <div className="pos-grid pos-grid-offers">
                {combos.map((combo) => renderOfferCard(combo, 'large'))}
              </div>
            )
          )}

          {/* PRODUCTS */}
          {activeTab === 'all' && (
            <>
              <div className="pos-section-head">
                <h2>{search.trim() ? `Results for “${search.trim()}”` : selectedCategory === 'All' ? 'All products' : selectedCategory}</h2>
                <span>
                  {filteredProducts.length} {filteredProducts.length === 1 ? 'item' : 'items'}
                  {search && filteredProducts.length > 0 && ', press Enter to add the first'}
                </span>
              </div>

              {filteredProducts.length === 0 ? (
                <div className="pos-empty">
                  <strong>{search ? 'No products match this search' : 'No products yet'}</strong>
                  <span>{search ? 'Check the spelling or try a shorter word.' : 'Add products from Products & Recipes.'}</span>
                </div>
              ) : (
                <div className="pos-grid">
                  {filteredProducts.map((product) => {
                    if (parcelProduct && product.id === parcelProduct.id) return null;
                    const inCart = cartQtyByProduct[product.id];
                    const weighted = product.pricing_type === 'weighted';
                    return (
                      <button
                        key={product.id}
                        type="button"
                        className={`pos-card ${inCart ? 'is-in-cart' : ''}`}
                        onClick={() => handleAdd(product)}
                      >
                        {inCart > 0 && (
                          <span className="pos-card-qty">{weighted ? `${inCart} g` : `×${inCart}`}</span>
                        )}
                        <span className="pos-card-icon" style={{ background: categoryTint(product.category || '') }}>
                          {categoryIcon(product.category)}
                        </span>
                        <span className="pos-card-name">{product.name}</span>
                        <span className="pos-card-cat">{product.category || 'Uncategorised'}</span>
                        {offerByProduct[product.id] && <span className="pos-card-offer">🎁 Offer</span>}
                        <span className="pos-card-foot">
                          <span className="pos-card-price">
                            {weighted ? `${fmt(product.selling_price)}/g` : fmt(Number(product.selling_price).toFixed(0))}
                          </span>
                          <span className="pos-add" aria-hidden="true">{weighted ? '⚖' : '+'}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
          <div className="pos-shortcuts" aria-hidden="true">
            <span><kbd>/</kbd> Search</span>
            <span><kbd>Enter</kbd> Add first result</span>
            <span><kbd>Esc</kbd> Clear search</span>
            <span><kbd>F2</kbd> Hold</span>
            <span><kbd>F8</kbd> Save</span>
            <span><kbd>F9</kbd> Save &amp; Print</span>
          </div>
        </section>

        {/* =================================================
            RIGHT — CHECKOUT
            ================================================= */}
        <aside className={`pos-checkout ${showCartMobile ? 'is-open' : ''}`}>

          {editingBill && (
            <div className="pos-edit-banner">
              <span>Editing <b>{editingBill.bill_no}</b>. Saving cancels it and creates a new bill.</span>
              <button type="button" onClick={() => { if (window.confirm(`Stop editing ${editingBill.bill_no}? It stays as it was.`)) { setEditingBill(null); clearCart(); } }}>Stop editing</button>
            </div>
          )}
          <div className="pos-checkout-head">
            <div>
              <h2>Current order</h2>
              <span>
                {cart.length === 0
                  ? 'No items yet'
                  : `${cart.length} ${cart.length === 1 ? 'line' : 'lines'}${nextOrderNo ? `, order #${nextOrderNo}` : ''}`}
              </span>
            </div>
            <div className="pos-head-actions">
              {cart.length > 0 && (
                <button
                  type="button"
                  className="pos-clear-btn"
                  onClick={() => { if (window.confirm('Clear all items from this order?')) clearCart(); }}
                >
                  Clear
                </button>
              )}
              <button type="button" className="pos-close-mobile" onClick={() => setShowCartMobile(false)} aria-label="Close order">
                ×
              </button>
            </div>
          </div>

          {lastBill && cart.length > 0 && (
            <div className="pos-last-slim">
              <span>Last bill <b>{lastBill.day_no ? `#${lastBill.day_no}` : lastBill.bill_no}</b></span>
              <button type="button" onClick={() => (lastBill.offline ? printLocalReceipt(lastBill.entry) : reprintBill(lastBill.id))}>Reprint</button>
              <button type="button" onClick={() => sendWhatsapp(lastBill)}>WhatsApp</button>
            </div>
          )}

          {/* ITEMS */}
          <div className="pos-items">
            {cart.length === 0 ? (
              <div className="pos-cart-empty">
                {lastBill ? (
                  <div className="pos-last-bill">
                    <div className="pos-last-head">
                      <span className="pos-last-check" aria-hidden="true">✓</span>
                      <div>
                        <strong>{lastBill.offline ? 'Saved offline, uploads automatically' : 'Sale complete'}</strong>
                        <span>{lastBill.day_no ? `#${lastBill.day_no}` : lastBill.bill_no}</span>
                      </div>
                    </div>
                    <div className="pos-last-lines">
                      {lastBill.items.map((item) => (
                        <div key={item.id}>
                          <span>{item.product_name} × {item.quantity}</span>
                          <b>{fmt2(item.price)}</b>
                        </div>
                      ))}
                    </div>
                    <div className="pos-last-total">
                      <span>Total</span>
                      <strong>{fmt2(lastBill.total_amount)}</strong>
                    </div>
                    <button type="button" className="pos-newbill-btn"
                      onClick={() => { setLastBill(null); if (searchRef.current) searchRef.current.focus(); }}>
                      ＋ New bill
                    </button>
                    <div className="pos-last-actions">
                      <button type="button" className="pos-ghost-btn" onClick={() => (lastBill.offline ? printLocalReceipt(lastBill.entry) : reprintBill(lastBill.id))}>
                        Reprint
                      </button>
                      <button type="button" className="pos-ghost-btn pos-wa-btn" onClick={() => sendWhatsapp(lastBill)}>
                        {lastBill.wa_phone ? 'Send on WhatsApp' : 'WhatsApp'}
                      </button>
                      {!lastBill.offline && (
                        <button type="button" className="pos-cancel-btn" onClick={() => cancelBill(lastBill.id)}>
                          Cancel bill
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <span className="pos-cart-empty-icon" aria-hidden="true">🍦</span>
                    <strong>Tap a product to start the order</strong>
                    <span>Or press / and type a name.</span>
                  </>
                )}
              </div>
            ) : (
              cart.map((item) => {
                if (parcelProduct && !item.combo_id && item.product_id === parcelProduct.id) return null;
                const key = getItemKey(item);
                const weighted = isWeighted(item);
                const combo = item.combo_id ? comboById[item.combo_id] : null;
                const freeItems = combo ? (combo.items || []).filter((i) => i.is_free) : [];
                return (
                  <div key={key} className="pos-line">
                    <div className="pos-line-row">
                      <div className="pos-line-info">
                        <strong>{combo ? combo.name : item.name}</strong>
                        <span>
                          {item.offer_choice_id
                            ? <b className="pos-offer-line-tag">🎁 {item.price > 0 ? `Offer ${fmt(item.price)}` : 'FREE with offer'}</b>
                            : weighted ? `${fmt(item.price)}/g` : `${fmt(item.price)} each`}
                          {!item.isCombo && !item.offer_choice_id && (
                            <button
                              type="button"
                              className={`pos-disc-link ${item.discount > 0 ? 'has-disc' : ''}`}
                              onClick={() => toggleDisc(key)}
                            >
                              {item.discount > 0 ? `${fmt(item.discount)} off` : 'Discount'}
                            </button>
                          )}
                        </span>
                      </div>

                      <div className="pos-qty">
                        <button
                          type="button"
                          onClick={() => changeQty(key, weighted ? -10 : -1)}
                          aria-label="Decrease quantity"
                        >
                          −
                        </button>
                        {weighted ? (
                          <button type="button" className="pos-qty-value is-editable" onClick={() => editWeight(item)} title="Change weight">
                            {item.quantity}g
                          </button>
                        ) : (
                          <span className="pos-qty-value">{item.quantity}</span>
                        )}
                        <button
                          type="button"
                          onClick={() => changeQty(key, weighted ? 10 : 1)}
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>

                      <strong className="pos-line-total">{fmt2(lineTotal(item))}</strong>

                      <button type="button" className="pos-remove" onClick={() => removeItem(key)} aria-label={`Remove ${item.name}`}>
                        ×
                      </button>
                    </div>

                    {weighted && (weighKey === key || !(item.quantity > 0)) && (
                      <div className="pos-weigh">
                        <label className="pos-weigh-input">
                          <span>Weight</span>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            inputMode="numeric"
                            autoFocus
                            placeholder="grams from scale"
                            value={item.quantity || ''}
                            onChange={(e) => setWeight(key, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && item.quantity > 0) setWeighKey(null); }}
                          />
                          <em>g</em>
                        </label>
                        <div className="pos-weigh-quick">
                          {[100, 250, 500, 750, 1000].map((g) => (
                            <button key={g} type="button" className={item.quantity === g ? 'is-on' : ''} onClick={() => setWeight(key, g)}>
                              {g >= 1000 ? `${g / 1000} kg` : `${g} g`}
                            </button>
                          ))}
                        </div>
                        <div className="pos-weigh-foot">
                          <span>
                            {item.quantity > 0
                              ? `${item.quantity} g × ${fmt(item.price)}/g = ${fmt2(item.quantity * item.price)}`
                              : 'Put it on the scale and type the grams'}
                          </span>
                          <button type="button" disabled={!(item.quantity > 0)} onClick={() => setWeighKey(null)}>Done</button>
                        </div>
                      </div>
                    )}

                    {freeItems.map((f) => (
                      <div key={f.product_name} className="pos-line-free">
                        <span><span className="pos-free">FREE</span> {f.product_name}</span>
                        <b>{fmt(0)}</b>
                      </div>
                    ))}

                    {!item.isCombo && discOpen[key] && (
                      <div className="pos-line-disc-row">
                        <span>Item discount ₹</span>
                        <input
                          type="number"
                          min="0"
                          inputMode="decimal"
                          autoFocus
                          value={item.discount || ''}
                          placeholder="0"
                          onChange={(e) => setLineDiscount(key, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') toggleDisc(key); }}
                        />
                        <button type="button" onClick={() => toggleDisc(key)}>Done</button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {cart.length > 0 && (
            <div className="pos-summary">
              <div className="pos-sum-row">
                <span>Subtotal</span>
                <b>{fmt2(grossTotal - parcelAmount)}</b>
              </div>
              {itemDiscountTotal > 0 && (
                <div className="pos-sum-row">
                  <span>Item discounts</span>
                  <b>−{fmt2(itemDiscountTotal)}</b>
                </div>
              )}
              {billDiscountAmount > 0 && (
                <div className="pos-sum-row">
                  <span>Bill discount{discountType === 'percent' ? ` (${billDiscount}%)` : ''}</span>
                  <b>−{fmt2(billDiscountAmount)}</b>
                </div>
              )}
              {parcelAmount > 0 && (
                <div className="pos-sum-row">
                  <span>Parcel charge</span>
                  <b>+{fmt2(parcelAmount)}</b>
                </div>
              )}
              <div className="pos-extras">
                <button
                  type="button"
                  className={`pos-extra-btn ${billDiscountAmount > 0 ? 'is-set' : ''} ${extrasOpen === 'discount' ? 'is-open' : ''}`}
                  onClick={() => setExtrasOpen(extrasOpen === 'discount' ? null : 'discount')}
                >
                  ％ Discount{billDiscountAmount > 0 ? ` −${fmt(billDiscountAmount)}` : ''}
                </button>
                <button
                  type="button"
                  className={`pos-extra-btn ${parcelAmount > 0 ? 'is-set' : ''} ${extrasOpen === 'parcel' ? 'is-open' : ''}`}
                  onClick={() => setExtrasOpen(extrasOpen === 'parcel' ? null : 'parcel')}
                >
                  🛍 Parcel{parcelAmount > 0 ? ` ${fmt(parcelAmount)}` : ''}
                </button>

                {extrasOpen === 'discount' && (
                  <div className="pos-pop">
                    <div className="pos-pop-row">
                      <span className="pos-pop-label">Bill discount</span>
                      <div className="pos-seg" role="group" aria-label="Discount type">
                        <button type="button" className={discountType === 'fixed' ? 'is-active' : ''} onClick={() => setDiscountType('fixed')}>₹</button>
                        <button type="button" className={discountType === 'percent' ? 'is-active' : ''} onClick={() => setDiscountType('percent')}>%</button>
                      </div>
                      <input
                        type="number"
                        min="0"
                        inputMode="decimal"
                        autoFocus
                        placeholder="0"
                        value={billDiscount}
                        onChange={(e) => setBillDiscount(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setExtrasOpen(null); }}
                        aria-label="Bill discount"
                      />
                    </div>
                    <div className="pos-pop-quick">
                      {(discountType === 'percent' ? [5, 10, 15, 20] : [10, 20, 50, 100]).map((v) => (
                        <button key={v} type="button" onClick={() => setBillDiscount(String(v))}>
                          {discountType === 'percent' ? `${v}%` : fmt(v)}
                        </button>
                      ))}
                    </div>
                    <div className="pos-pop-foot">
                      <button type="button" className="pos-pop-clear" onClick={() => { setBillDiscount(''); setExtrasOpen(null); }}>Remove</button>
                      <button type="button" className="pos-pop-done" onClick={() => setExtrasOpen(null)}>Done</button>
                    </div>
                  </div>
                )}

                {extrasOpen === 'parcel' && (
                  <div className="pos-pop">
                    {parcelProduct ? (
                      <>
                        <div className="pos-pop-row">
                          <span className="pos-pop-label">Parcel charge ₹</span>
                          <input
                            type="number"
                            min="0"
                            step={parcelPrice}
                            inputMode="decimal"
                            autoFocus
                            placeholder="0"
                            value={parcelAmount || ''}
                            onChange={(e) => setParcelAmount(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setExtrasOpen(null); }}
                            aria-label="Parcel charge"
                          />
                        </div>
                        <div className="pos-pop-quick">
                          {[5, 10, 20, 30].map((v) => (
                            <button key={v} type="button" onClick={() => setParcelAmount(v)}>{fmt(v)}</button>
                          ))}
                        </div>
                        {parcelPrice !== 1 && (
                          <p className="pos-pop-note">Goes up in steps of {fmt(parcelPrice)}. Set the PARCEL CHARGE product price to ₹1 to enter any amount.</p>
                        )}
                        <div className="pos-pop-foot">
                          <button type="button" className="pos-pop-clear" onClick={() => { setParcelQty(0); setExtrasOpen(null); }}>Remove</button>
                          <button type="button" className="pos-pop-done" onClick={() => setExtrasOpen(null)}>Done</button>
                        </div>
                      </>
                    ) : (
                      <p className="pos-pop-note">
                        To use parcel charges, add a product named <b>PARCEL CHARGE</b> with price <b>₹1</b> (category Parcel) in Products &amp; Recipes.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* FOOTER — PAYMENT + TOTAL + ACTIONS (always visible) */}
          <div className="pos-footer">
            {cart.length > 0 && (
              <div className="pos-f-pay">
                <div className="pos-f-modes">
                  {!isSplitPayment ? (
                    PAYMENT_OPTIONS.map((payment) => (
                      <button
                        key={payment.id}
                        type="button"
                        className={paymentMode === payment.id ? 'pos-f-mode is-active' : 'pos-f-mode'}
                        onClick={() => setPaymentMode(payment.id)}
                        aria-pressed={paymentMode === payment.id}
                      >
                        <span aria-hidden="true">{payment.icon}</span> {payment.label}
                      </button>
                    ))
                  ) : (
                    <span className="pos-f-split-title">Split payment</span>
                  )}
                  <label className="pos-switch pos-switch-dark" title="Split between cash, UPI and card">
                    <input
                      type="checkbox"
                      checked={isSplitPayment}
                      onChange={(e) => {
                        setIsSplitPayment(e.target.checked);
                        if (!e.target.checked) {
                          setSplitAmounts({ cash: '', upi: '', card: '' });
                        }
                      }}
                    />
                    <span className="pos-switch-track" aria-hidden="true" />
                    Split
                  </label>
                </div>

                {isSplitPayment ? (
                  <div className="pos-f-split">
                    {['cash', 'upi', 'card'].map((mode) => (
                      <label key={mode}>
                        <span>{mode === 'upi' ? 'UPI' : mode.charAt(0).toUpperCase() + mode.slice(1)}</span>
                        <input
                          type="number"
                          min="0"
                          inputMode="decimal"
                          placeholder="0"
                          value={splitAmounts[mode]}
                          onChange={(e) => setSplitAmounts({ ...splitAmounts, [mode]: e.target.value })}
                        />
                      </label>
                    ))}
                    <div className={`pos-f-note ${Math.abs(splitLeft) < 0.01 ? 'is-good' : 'is-bad'}`}>
                      {Math.abs(splitLeft) < 0.01
                        ? 'Amounts match the total'
                        : splitLeft > 0
                          ? `${fmt2(splitLeft)} still to collect`
                          : `${fmt2(-splitLeft)} more than the total`}
                    </div>
                  </div>
                ) : paymentMode === 'cash' && (
                  <div className="pos-f-cash">
                    <div className="pos-f-cash-input">
                      <span>Cash ₹</span>
                      <input
                        type="number"
                        min="0"
                        inputMode="decimal"
                        placeholder="received"
                        value={cashReceived}
                        onChange={(e) => setCashReceived(e.target.value)}
                        aria-label="Cash received"
                      />
                    </div>
                    {quickCash.map((v, i) => (
                      <button key={v} type="button" className="pos-f-quick" onClick={() => setCashReceived(String(v))}>
                        {i === 0 ? 'Exact' : fmt(v)}
                      </button>
                    ))}
                    {cashReceived !== '' && (
                      <div className={`pos-f-note ${changeDue < 0 ? 'is-bad' : 'is-good'}`}>
                        {changeDue < 0 ? `Still short ${fmt2(-changeDue)}` : `Change to give ${fmt2(changeDue)}`}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="pos-wa-row">
              <span aria-hidden="true">📱</span>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={15}
                placeholder="Customer WhatsApp no. (optional)"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value.replace(/[^\d+ ]/g, ''))}
                aria-label="Customer WhatsApp number"
              />
            </div>
            <div className="pos-total">
              <span>Total{roundOff !== 0 && <small style={{ display: 'block', fontSize: 12, opacity: 0.7, fontWeight: 600 }}>round off {roundOff > 0 ? '+' : '−'}{fmt2(Math.abs(roundOff))}</small>}</span>
              <strong>{fmt2(total)}</strong>
            </div>
            <div className="pos-actions">
              <button type="button" className="pos-hold-btn" onClick={holdBill} disabled={saving || cart.length === 0}>
                Hold <kbd>F2</kbd>
              </button>
              <button
                type="button"
                className="pos-save-btn"
                onClick={() => completeBill(false)}
                disabled={saving || cart.length === 0}
              >
                {saving ? '…' : 'Save'}
                {!saving && <kbd>F8</kbd>}
              </button>
              <button
                type="button"
                className="pos-complete-btn"
                onClick={() => completeBill(true)}
                disabled={saving || cart.length === 0}
              >
                {saving ? 'Saving…' : 'Save & Print'}
                {!saving && <kbd>F9</kbd>}
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* MOBILE ORDER BAR */}
      <button
        type="button"
        className={`pos-mobile-bar ${cart.length === 0 ? 'is-empty' : ''}`}
        onClick={() => setShowCartMobile(true)}
      >
        <span>{cart.length === 0 ? 'No items yet' : `${cart.length} ${cart.length === 1 ? 'line' : 'lines'}`}</span>
        <strong>{fmt2(total)}</strong>
        <span className="pos-mobile-cta">View order</span>
      </button>
    </div>
  );
}
